import type { OdinKey } from '@myosh/logic';
import { useEffect, useRef, useState } from 'react';
import { Observable, type Subscription } from 'rxjs';
import type { JsonDataWrapper, OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import { OdinDataSender } from '../../common/data-retrieval/odin-data-sender';
import type { JsonData } from '../generic-types';
import type { DynamicFormFieldType } from './dynamic-form.interfaces';

/**
 * Resolves a `data` prop that may be either a static `JsonData` array or an `OdinDataRetrieval`
 * fetcher into a static array, so the underlying field component can stay presentation-only.
 *
 * Use at the Hf wrapper layer for non-paginated fields whose `data` may be injected by
 * `DynamicForm`'s `dataRetrieval` prop. The async path subscribes once on mount and stores the result.
 * Not suitable for paginated or search-driven fetches.
 */
export const useResolveDynamicFormData = (
  data: JsonData | OdinDataRetrieval | undefined,
  fieldId: OdinKey | undefined,
  fieldType: DynamicFormFieldType
): JsonData | undefined => {
  const [asyncData, setAsyncData] = useState<JsonData>();
  const subscriptionRef = useRef<Subscription | null>(null);

  const useStaticData = !data || Array.isArray(data);

  useEffect(() => {
    if (useStaticData) return;

    subscriptionRef.current?.unsubscribe();
    let sender: OdinDataSender<JsonDataWrapper> | undefined;
    const observable = new Observable<JsonDataWrapper>((subscriber) => {
      sender = new OdinDataSender(subscriber);
    });
    subscriptionRef.current = observable.subscribe({
      next: (wrapper) => setAsyncData(wrapper?.data ?? []),
    });
    if (sender) {
      data.getData(sender, {
        page: 1,
        fieldId,
        fieldType,
      });
    }

    return () => {
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [data, fieldId, fieldType, useStaticData]);

  // `useStaticData` guarantees `data` is JsonData | undefined here; the narrowing
  // isn't carried through the const under OL's non-strict config, so assert it.
  return useStaticData ? (data as JsonData | undefined) : asyncData;
};
